/**
 * 未進貨訂單-供應商 Unreceived-by-Vendor report (S11.1).
 *
 * Open purchase orders (status ≠ 已進貨), grouped by vendor — the goods a
 * PO has ordered but not yet received into the flow. Drill-through opens the
 * PO detail. Per-currency open totals only (never cross-currency summed).
 */

import { useMemo } from 'react';
import { Card, Col, Row, Statistic, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import { formatAmount, formatMoney } from '@/components/salesOrders/components/soFormat';
import type { PurchaseOrder, PurchaseOrderStatus } from '@/types/api/purchaseOrder';
import { useReportPurchaseOrders } from '../reportsData';
import { ReportSyncNote, ReportToolbar } from '../reportsShared';
import { exportReportCsv } from '../reportsCsv';

const { Text } = Typography;

const UnreceivedReport = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['reports', 'procurement', 'common']);
  const { token } = theme.useToken();
  const { rows: purchaseOrders, isLoading, error, refresh } = useReportPurchaseOrders();

  // Open = not yet received; sorted by vendor so the report reads by-vendor.
  const { rows, generatedAt } = useMemo(
    () => ({
      rows: purchaseOrders
        .filter((po) => po.status !== 'received')
        .sort((a, b) => a.vendorName.localeCompare(b.vendorName) || a.poNumber.localeCompare(b.poNumber)),
      generatedAt: new Date().toISOString(),
    }),
    [purchaseOrders]
  );

  const totals = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((po) => map.set(po.currency, (map.get(po.currency) ?? 0) + po.totalAmount));
    return [...map.entries()].map(([currency, amount]) => ({ currency, amount }));
  }, [rows]);

  const columns: TableProps<PurchaseOrder>['columns'] = [
    {
      title: t('reports:reports.unreceived.columns.po'),
      key: 'po',
      width: 160,
      fixed: 'left',
      render: (_: unknown, po) => (
        <a
          style={{ color: token.colorPrimary, cursor: 'pointer', fontWeight: token.fontWeightStrong }}
          onClick={() => navigate(`/auth/purchase-orders/${po.id}`)}
        >
          {po.poNumber}
        </a>
      ),
    },
    {
      title: t('reports:reports.unreceived.columns.vendor'),
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 200,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('reports:reports.unreceived.columns.sourceSo'),
      dataIndex: 'sourceSoNumber',
      key: 'sourceSoNumber',
      width: 160,
      render: (value: string | null | undefined) => renderDash(value),
    },
    {
      title: t('reports:reports.unreceived.columns.orderDate'),
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 120,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('reports:reports.unreceived.columns.expectedDate'),
      dataIndex: 'expectedDeliveryDate',
      key: 'expectedDeliveryDate',
      width: 130,
      render: (value: string | null) => renderDash(value),
    },
    {
      title: t('reports:reports.unreceived.columns.amount'),
      key: 'amount',
      width: 150,
      align: 'right',
      render: (_: unknown, po) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(po.currency, po.totalAmount)}
        </span>
      ),
    },
    {
      title: t('reports:reports.unreceived.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: PurchaseOrderStatus) => <Tag>{t(`procurement:status.${status}`)}</Tag>,
    },
  ];

  const handleExport = () => {
    exportReportCsv<PurchaseOrder>(
      'unreceived-vendor.csv',
      [
        { header: t('reports:reports.unreceived.columns.po'), value: (po) => po.poNumber },
        { header: t('reports:reports.unreceived.columns.vendor'), value: (po) => po.vendorName },
        { header: t('reports:reports.unreceived.columns.sourceSo'), value: (po) => po.sourceSoNumber ?? '' },
        { header: t('reports:reports.unreceived.columns.orderDate'), value: (po) => po.orderDate },
        { header: t('reports:reports.unreceived.columns.expectedDate'), value: (po) => po.expectedDeliveryDate ?? '' },
        { header: t('reports:reports.common.currency'), value: (po) => po.currency },
        { header: t('reports:reports.unreceived.columns.amount'), value: (po) => formatAmount(po.totalAmount) },
        { header: t('reports:reports.unreceived.columns.status'), value: (po) => t(`procurement:status.${po.status}`) },
      ],
      rows
    );
  };

  return (
    <>
      <ReportToolbar
        formula={t('reports:reports.unreceived.formula')}
        onExport={handleExport}
        exportDisabled={rows.length === 0}
      />

      <Row gutter={[12, 12]} style={{ marginBottom: token.marginLG }}>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title={t('reports:reports.unreceived.stats.openPos')}
              value={rows.length}
            />
          </Card>
        </Col>
        {totals.map((entry) => (
          <Col xs={12} lg={6} key={entry.currency}>
            <Card size="small">
              <Statistic
                title={t('reports:reports.unreceived.stats.openAmount', { currency: entry.currency })}
                value={formatAmount(entry.amount)}
              />
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {entry.currency}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>

      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('reports:reports.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<PurchaseOrder>
          rowKey="id"
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          locale={{ emptyText: <ListEmptyState title={t('reports:reports.unreceived.empty')} /> }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1070 }}
        />
      </TableContainer>
      <ReportSyncNote syncedAt={generatedAt} />
    </>
  );
};

export default UnreceivedReport;
